/**
 * Bottom sheet for sending tickets with optional custom message.
 */

import { useState } from "react";
import { haptic } from "../../../../lib/telegram.ts";

export interface SendSheetProps {
  /** Is the sheet open */
  isOpen: boolean;
  /** Number of tickets to send */
  ticketCount: number;
  /** Callback to close the sheet */
  onClose: () => void;
  /** Callback when send is confirmed */
  onConfirm: (customMessage?: string) => void;
  /** Is currently processing */
  isProcessing?: boolean;
  /** Color variant: light (default) or dark */
  variant?: "light" | "dark";
}

export function SendSheet({
  isOpen,
  ticketCount,
  onClose,
  onConfirm,
  isProcessing = false,
  variant = "light",
}: SendSheetProps) {
  const [customMessage, setCustomMessage] = useState("");

  if (!isOpen) return null;

  const handleConfirm = () => {
    haptic("medium");
    onConfirm(customMessage.trim() || undefined);
  };

  const handleClose = () => {
    setCustomMessage("");
    onClose();
  };

  const isDark = variant === "dark";

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 ${isDark ? "bg-black/60" : "bg-black/40"}`}
        onClick={handleClose}
        aria-hidden
      />

      {/* Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 z-50 max-h-[85vh] overflow-y-auto rounded-t-[20px] shadow-[0_-4px_24px_rgba(0,0,0,0.12)] safe-area-bottom ${
          isDark ? "bg-slate-900" : "bg-[var(--app-bg)]"
        }`}
      >
        {/* Handle */}
        <div
          className={`sticky top-0 flex justify-center py-3 ${isDark ? "bg-slate-900" : "bg-[var(--app-bg)]"}`}
        >
          <div
            className={`w-10 h-1 rounded-full ${isDark ? "bg-white/20" : "bg-slate-200"}`}
            aria-hidden
          />
        </div>

        <div className="px-4 pb-6">
          <h3
            className={`text-[17px] font-semibold mb-1 ${isDark ? "text-white" : "text-slate-900"}`}
          >
            Отправить билеты
          </h3>
          <p
            className={`text-[14px] mb-4 ${isDark ? "text-white/50" : "text-slate-500"}`}
          >
            {ticketCount === 1
              ? "Отправить билет пользователю"
              : `Отправить ${ticketCount} билет${ticketCount < 5 ? "а" : "ов"} пользователю`}
          </p>

          {/* Custom message textarea */}
          <div className="mb-4">
            <label
              className={`block text-[13px] font-medium mb-2 ${isDark ? "text-white/70" : "text-slate-700"}`}
            >
              Сообщение (необязательно)
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Если оставить пустым, будет отправлен стандартный текст с информацией о мероприятии"
              className={`w-full h-28 px-4 py-3 rounded-xl text-[15px] resize-none focus:outline-none ${
                isDark
                  ? "bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-white/30"
                  : "bg-slate-100 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
              }`}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isProcessing}
              className={`flex-1 min-h-[50px] rounded-xl text-[15px] font-medium disabled:opacity-50 ${
                isDark
                  ? "bg-white/10 text-white active:bg-white/15"
                  : "bg-slate-100 text-slate-700 active:bg-slate-200"
              }`}
            >
              Отмена
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isProcessing}
              className="flex-1 min-h-[50px] rounded-xl bg-blue-500 text-white text-[15px] font-medium active:opacity-90 disabled:opacity-50 flex items-center justify-center"
            >
              {isProcessing ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : customMessage.trim() ? (
                "Отправить с сообщением"
              ) : (
                "Отправить"
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
